/**
 * PendingApprovalPage
 *
 * P-017: shown when the email domain already belongs to an organization. The user joins
 * as a pending member and waits for an administrator (design-of-record
 * ui_kits/login/waitlist.html, pending approval). OAuth users arrive here directly;
 * email users arrive after set-password. No resend action: there is nothing to verify.
 */

import { Link, useSearchParams } from 'react-router'
import { Trans, useTranslation } from 'react-i18next'
import { ArrowLeft, Check, Users } from 'lucide-react'
import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout'

// Cap the organization name taken from the URL so it cannot flood the DOM.
const MAX_ORG_NAME_LENGTH = 200
const SUPPORT_EMAIL = 'support@agion.ai'

function sanitizeOrgName(orgParam: string | null): string {
  if (!orgParam) return ''
  // React escapes the text; only the length needs handling.
  return orgParam.length > MAX_ORG_NAME_LENGTH
    ? `${orgParam.substring(0, MAX_ORG_NAME_LENGTH)}...`
    : orgParam
}

export function PendingApprovalPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const organizationName = sanitizeOrgName(searchParams.get('org'))
  const nextSteps = [t('waitlist.pendingStep1'), t('waitlist.pendingStep2'), t('waitlist.pendingStep3')]

  return (
    <AuthSplitLayout>
      <div className="lp-pop">
        <div className="lp-state">
          <div className="lp-badge lp-badge-brand">
            <Users size={24} aria-hidden="true" />
          </div>
          <h2 className="lp-state-title">{t('waitlist.pendingApprovalTitle')}</h2>
          <p className="lp-state-msg">
            {organizationName ? (
              <Trans
                i18nKey="waitlist.pendingApprovalBodyRich"
                values={{ organization: organizationName }}
                components={{ b: <strong className="lp-emph" /> }}
              />
            ) : (
              t('waitlist.pendingApprovalBodyGeneric')
            )}
          </p>
        </div>

        <div className="lp-panel">
          <div className="lp-panel-title">{t('waitlist.whatHappensNext')}</div>
          <ul className="lp-panel-list">
            {nextSteps.map((step) => (
              <li key={step} className="lp-panel-item">
                <Check size={16} aria-hidden="true" />
                {step}
              </li>
            ))}
          </ul>
        </div>

        <p className="lp-support">
          {t('waitlist.wrongOrg')}{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>

        <div className="lp-state">
          <Link to="/login" className="lp-back">
            <ArrowLeft size={16} aria-hidden="true" />
            {t('waitlist.backToSignIn')}
          </Link>
        </div>
      </div>
    </AuthSplitLayout>
  )
}

export default PendingApprovalPage
